import React, {
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import { CSSTransition } from 'react-transition-group';

import useBooleanState from '@/hooks/useBooleanState';
import useElementEvent from '@/hooks/useElementEvent';
import { SlideItem } from '@/types/common';

import ObjectSlider from '@/components/ObjectSliderModal';
import ObjectGallery from '@/components/ObjectGallery';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import Icon from '@/components/UI/Icon';

interface ObjectSliderModalProps {
  buttonId: string;
  slides: SlideItem[];
  title: string;
  gallery: string;
  onInit?: (open: (index?: number) => void) => void;
}

const ObjectSliderModal = ({
  buttonId,
  slides,
  title,
  gallery,
  onInit
}: ObjectSliderModalProps) => {
  const [active, setActive] = useState<number | null>(null);

  const {
    isOpen: isModalOpen,
    open: openModal,
    close: closeModal
  } = useBooleanState();

  const {
    isOpen: isGalleryOpen,
    toggle: toggleGallery,
    close: closeGallery
  } = useBooleanState();

  useElementEvent(buttonId, openModal);

  useEffect(() => {
    onInit?.((index?: number) => {
      if (index != null) { setActive(index); }
      openModal();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onClick = useCallback((slide: SlideItem, index: number) => {
    setActive(index);
    closeGallery();
  }, [closeGallery]);

  return (
    <Modal
      overlayClassName="object-modal-overlay"
      isOpen={isModalOpen}
      onRequestClose={closeModal}
    >
      <CSSTransition
        in={!isGalleryOpen}
        timeout={500}
      >
        <div className="object-slider">
          <div className="object-modal-content">
            <ObjectSlider
              active={active}
              slides={slides}
            />
          </div>
        </div>
      </CSSTransition>

      <CSSTransition
        in={isGalleryOpen}
        timeout={500}
      >
        <div className="object-gallery">
          <div className="object-modal-content">
            <ObjectGallery
              slides={slides}
              title={title}
              onClick={onClick}
            />
          </div>
        </div>
      </CSSTransition>

      <div className="object-modal-wrapper">
        <Button
          aria-label={gallery}
          className="object-modal-toggle"
          aria-pressed={isGalleryOpen}
          onClick={toggleGallery}
        >
          <Icon
            name="Gallery"
            width={36}
            height={36}
            color="#FFF"
          />

          <span className="content-subtitle">
            {gallery}
          </span>
        </Button>

        <Button
          aria-label="Close"
          className="ReactModal__Close"
          onClick={isGalleryOpen ? closeGallery : closeModal}
        >
          <Icon
            name="Close"
            width={32}
            height={32}
            color="#FFF"
          />
        </Button>
      </div>
    </Modal>
  );
};

export default memo(ObjectSliderModal);
